import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad, useTaxonomy } from "../lib/api";
import { Field, Loading, PageTitle, StatusBadge } from "../components/kit";
import { useMe } from "../components/MeContext";

const blank = { business_name: "", category_ids: [] as string[], area_ids: [] as string[], experience_years: 0, experience_summary: "", availability: "available" };

function Checks({ items, value, onChange, idPrefix }: { items: any[]; value: string[]; onChange: (v: string[]) => void; idPrefix: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((x) => (
        <label key={x.id} className={`badge cursor-pointer ${value.includes(x.id) ? "badge-blue" : ""}`}>
          <input type="checkbox" className="mr-1" checked={value.includes(x.id)} onChange={() => onChange(value.includes(x.id) ? value.filter((v) => v !== x.id) : [...value, x.id])} data-testid={`${idPrefix}-${x.id}`} />{x.name}
        </label>
      ))}
    </div>
  );
}

function ApplyForm({ onDone }: { onDone: () => void }) {
  const api = useApi();
  const { categories, areas } = useTaxonomy();
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (f.business_name.trim().length < 2 || !f.category_ids.length || !f.area_ids.length) { toast.error("Add a business name, at least one trade and one area"); return; }
    setBusy(true);
    try { await api("post", "/applications", f); toast.success("Application submitted for review"); onDone(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card space-y-4" data-testid="apply-form" noValidate>
      <Field label="Business name *" id="ap-name"><input id="ap-name" className="input" maxLength={120} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="apply-business-name-input" /></Field>
      <div><p className="mb-1 text-sm font-semibold">Trades *</p><Checks items={categories} value={f.category_ids} onChange={(v) => setF({ ...f, category_ids: v })} idPrefix="apply-trade" /></div>
      <div><p className="mb-1 text-sm font-semibold">Service areas *</p><Checks items={areas} value={f.area_ids} onChange={(v) => setF({ ...f, area_ids: v })} idPrefix="apply-area" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Years of experience" id="ap-years"><input id="ap-years" type="number" min={0} max={80} className="input" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: Math.max(0, Math.min(80, parseInt(e.target.value || "0", 10))) })} data-testid="apply-years-input" /></Field>
        <Field label="Availability" id="ap-av"><select id="ap-av" className="input" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} data-testid="apply-availability-select">
          <option value="available">Available</option><option value="limited">Limited</option><option value="unavailable">Not taking work yet</option></select></Field>
      </div>
      <Field label="Experience summary (seen by Verge operations only)" id="ap-sum"><textarea id="ap-sum" rows={3} maxLength={500} className="input" value={f.experience_summary} onChange={(e) => setF({ ...f, experience_summary: e.target.value })} data-testid="apply-summary-input" /></Field>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="apply-submit-button">{busy ? "Submitting…" : "Submit application"}</button>
    </form>
  );
}

export default function Apply() {
  const { isLoaded, isSignedIn } = useAuth();
  const { me } = useMe();
  const { data, reload } = useLoad<any[]>(isSignedIn ? "/applications/mine" : null);
  const open = data?.some((a) => ["pending", "in_review"].includes(a.status));
  const ineligible = me && (me.kind === "ops" || me.org_id);
  return (
    <div className="mx-auto max-w-2xl px-4 py-10" data-testid="apply-page">
      <PageTitle kicker="For service pros" title="Apply to join Verge" />
      <p className="mb-6 text-sm text-[var(--muted)]">Operations reviews every application. Approval sets up a pending business profile; you are then onboarded separately. Nothing is published automatically.</p>
      {!isLoaded ? <Loading /> : !isSignedIn ? (
        <div className="card text-sm" data-testid="apply-sign-in-required"><p>Sign in first so we can tie the application to your verified account.</p><Link to="/sign-in" className="btn btn-primary mt-3" data-testid="apply-sign-in-link">Sign In</Link></div>
      ) : (
        <div className="space-y-6">
          {!!data?.length && (
            <ul className="space-y-2" data-testid="apply-my-applications">
              {data.map((a) => <li key={a.id} className="card flex flex-wrap items-center justify-between gap-2 text-sm"><span><strong>{a.business_name}</strong> · {fmt(a.created_at)}</span><StatusBadge value={a.status} testId={`apply-status-${a.id}`} /></li>)}
            </ul>
          )}
          {ineligible ? <p className="card text-sm" data-testid="apply-ineligible">Your account already belongs to an organization or operations team.</p>
            : open ? <p className="card text-sm" data-testid="apply-under-review">Your application is being reviewed. We will follow up through onboarding.</p>
              : <ApplyForm onDone={reload} />}
        </div>
      )}
    </div>
  );
}
